import clsx from "clsx";
import { AnimatePresence, motion, Variant, Variants } from "framer-motion";
import { merge } from "lodash-es";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";

import { DefaultComponentPropsWith } from "@/interfaces/common";

import { Button } from "../Button";
import { Icon } from "../Icon";

export type CollapseHandle = {
  open: () => void;
  close: () => void;
  toggle: () => void;
};

type Props = Readonly<
  DefaultComponentPropsWith<{
    title: string | React.ReactNode;
    id?: string;
    activeClassName?: string;
    variants?: {
      [Key in "closed" | "open"]?: Variant;
    };
    disabled?: boolean;
    shouldForceOpen?: boolean;
  }>
>;

export const Collapse = forwardRef<CollapseHandle, Props>(function Collapse(
  { children, className, title, id, activeClassName, variants: customVariants, disabled, shouldForceOpen },
  ref,
) {
  const [isOpen, setIsOpen] = useState(false);

  const variants: Variants = merge(
    {
      closed: { opacity: 0, height: 0, y: -10 },
      open: { opacity: 1, height: "auto", y: 0 },
    },
    customVariants,
  );

  const open = () => setIsOpen(true);
  const toggle = () => setIsOpen((currentIsOpen) => !currentIsOpen);
  const close = () => setIsOpen(false);

  useImperativeHandle(ref, () => ({ open, close, toggle }), [shouldForceOpen]);

  useEffect(() => {
    if (shouldForceOpen) {
      setIsOpen(true);
    }
  }, [shouldForceOpen]);

  return (
    <section
      id={id}
      className={clsx(
        "p-10 bg-white",
        {
          "open group": isOpen,
          [activeClassName ?? ""]: isOpen,
        },
        className,
      )}
    >
      <div className={clsx("flex items-center justify-between gap-4")}>
        {typeof title === "string" ? <h3>{title}</h3> : title}

        <Button variant="secondary" onClick={toggle} disabled={disabled || shouldForceOpen} fit>
          <motion.div
            initial={false}
            animate={{ rotate: isOpen ? 0 : -180 }}
            transition={{ duration: 0.2 }}
            className={clsx({
              "pointer-events-none opacity-30": disabled,
            })}
          >
            <Icon
              name="KeyboardArrowUp"
              size="80"
              className={clsx({
                "text-primary": isOpen,
              })}
            />
          </motion.div>
        </Button>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            variants={variants}
            initial="closed"
            animate={isOpen ? "open" : "closed"}
            exit="closed"
            transition={{ duration: 0.2, ease: "easeOut" }}
          >
            <div className="mt-6">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
});
